import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Mail, Phone, Building2, Search, Edit2, Trash2 } from 'lucide-react';
import { apiFetch } from '../../utils/api';

const ContactsManagement: React.FC = () => {
    const [contacts, setContacts] = useState<any[]>([]);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editData, setEditData] = useState<any>(null);

    const [newContact, setNewContact] = useState({
        account_id: '', name: '', email: '', phone: '', role: ''
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const [contactsRes, accountsRes] = await Promise.all([
                apiFetch('/crm/contacts'),
                apiFetch('/accounts')
            ]);
            if (contactsRes.ok && accountsRes.ok) {
                setContacts(await contactsRes.json());
                setAccounts(await accountsRes.json());
            }
        } catch (err) {
            console.error('Error fetching contacts:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAddContact = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/crm/contacts', {
                method: 'POST',
                body: JSON.stringify(newContact)
            });
            if (res.ok) {
                setShowAddModal(false);
                setNewContact({ account_id: '', name: '', email: '', phone: '', role: '' });
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const handleUpdateContact = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editData) return;
        try {
            const res = await apiFetch(`/crm/contacts/${editData.id}`, {
                method: 'PUT',
                body: JSON.stringify(editData)
            });
            if (res.ok) {
                setShowEditModal(false);
                fetchData();
            }
        } catch (err) { console.error(err); }
    };

    const handleDeleteContact = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;
        try {
            const res = await apiFetch(`/crm/contacts/${id}`, { method: 'DELETE' });
            if (res.ok) fetchData();
        } catch (err) { console.error(err); }
    };

    const filteredContacts = contacts.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        className="w-full pl-10 pr-4 py-2 border rounded-xl bg-gray-50 focus:bg-white transition-colors outline-none focus:ring-2 focus:ring-blue-500/20"
                        placeholder="Search contacts..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/20"
                >
                    <Plus size={20} /> Add Contact
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-500" size={40} /></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredContacts.map((contact, i) => (
                        <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:border-blue-200 transition-colors group relative">
                            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => { setEditData(contact); setShowEditModal(true); }} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100">
                                    <Edit2 size={14} />
                                </button>
                                <button onClick={() => handleDeleteContact(contact.id)} className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                            <div className="flex items-start justify-between mb-4">
                                <div className="h-12 w-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-lg">
                                    {contact.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-50 text-blue-600 uppercase tracking-wider">
                                    {contact.role || 'Contact'}
                                </span>
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-1">{contact.name}</h3>
                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Building2 size={16} />
                                    <span>{accounts.find(a => a.id === contact.account_id)?.name || 'No Account'}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Mail size={16} />
                                    <span>{contact.email}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                    <Phone size={16} />
                                    <span>{contact.phone}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add/Edit Modal */}
            {(showAddModal || showEditModal) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">{showEditModal ? 'Edit Contact' : 'Add New Contact'}</h3>
                        <form onSubmit={showEditModal ? handleUpdateContact : handleAddContact} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Account</label>
                                <select
                                    className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                    value={showEditModal ? editData?.account_id : newContact.account_id}
                                    onChange={e => showEditModal ? setEditData({ ...editData, account_id: e.target.value }) : setNewContact({ ...newContact, account_id: e.target.value })}
                                    required
                                >
                                    <option value="">Select Account</option>
                                    {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name</label>
                                <input
                                    className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                    value={showEditModal ? editData?.name : newContact.name}
                                    onChange={e => showEditModal ? setEditData({ ...editData, name: e.target.value }) : setNewContact({ ...newContact, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Role/Title</label>
                                    <input
                                        className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                        value={showEditModal ? editData?.role : newContact.role}
                                        onChange={e => showEditModal ? setEditData({ ...editData, role: e.target.value }) : setNewContact({ ...newContact, role: e.target.value })}
                                        placeholder="e.g. CTO"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
                                    <input
                                        className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                        value={showEditModal ? editData?.phone : newContact.phone}
                                        onChange={e => showEditModal ? setEditData({ ...editData, phone: e.target.value }) : setNewContact({ ...newContact, phone: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address</label>
                                <input
                                    type="email"
                                    className="w-full border p-3 rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-blue-500/20"
                                    value={showEditModal ? editData?.email : newContact.email}
                                    onChange={e => showEditModal ? setEditData({ ...editData, email: e.target.value }) : setNewContact({ ...newContact, email: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => { setShowAddModal(false); setShowEditModal(false); }} className="flex-1 px-6 py-3 border border-gray-200 rounded-xl font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
                                <button type="submit" className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25">
                                    {showEditModal ? 'Update Contact' : 'Create Contact'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContactsManagement;
